import { type FC } from "react";
import { ArrowRight, Check } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";

interface ConfirmationStepProps {
  clientFirstName: string;
  inviteLink: string;
  firstClientOfferApplied: boolean;
  onDone: () => void;
  onInviteAnother: () => void;
  onStartRecord: () => void;
}

const getNextSteps = (firstClientOffer: boolean) => [
  {
    number: 1,
    title: "John reviews and activates",
    body: firstClientOffer
      ? "John accepts the invite and selects a plan. Estate Setup and Asset Search are covered by your first-client credit"
      : "John accepts the invite and selects a plan",
  },
  {
    number: 2,
    title: "Elayne guides the intake",
    body: "Elayne prompts John for the next document the estate is likely to need and builds the source-linked estate record as each one arrives",
  },
  {
    number: 3,
    title: "You are notified along the way",
    body: "Know when John activates, watch the record build, and see when it's attorney-ready. No chasing, no status calls",
  },
];

export const ConfirmationStep: FC<ConfirmationStepProps> = ({
  clientFirstName,
  inviteLink,
  firstClientOfferApplied,
  onDone,
  onInviteAnother,
  onStartRecord,
}) => {
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      enqueueSnackbar("Invite link copied.", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy link. Please try again.", { variant: "error" });
    }
  };

  const nextSteps = getNextSteps(firstClientOfferApplied).map((step) => ({
    ...step,
    title: step.title.replace(/John/g, clientFirstName),
    body: step.body.replace(/John/g, clientFirstName),
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand">
            <Check className="h-6 w-6 text-white" />
          </div>
          <div className="flex flex-col items-center gap-1">
            <h4 className="text-center text-xl font-semibold">Invite sent to {clientFirstName}</h4>
            <p className="text-center text-sm text-secondary">
              You can also share {clientFirstName}&apos;s invite link yourself:{" "}
              <button
                type="button"
                onClick={handleCopyLink}
                className="font-medium text-brand underline"
              >
                Copy link
              </button>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-primary bg-background p-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">
                Start {clientFirstName}&apos;s estate record
              </span>
              <Tag color="white">Optional</Tag>
            </div>
            <p className="text-sm text-secondary">
              Add documents, decedent details, or contacts already in your file. {clientFirstName}{" "}
              starts from where you left off, not from scratch
            </p>
          </div>
          <Button variant="outline" className="w-fit" onClick={onStartRecord}>
            Add to record <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          <span className="text-sm font-semibold text-secondary">What happens next</span>
          <div className="flex flex-col gap-4">
            {nextSteps.map((step) => (
              <div key={step.number} className="flex gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background text-sm font-medium">
                  {step.number}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">{step.title}</span>
                  <span className="text-sm">{step.body}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-6 border-t border-primary px-6 py-4">
        <Button variant="outline" onClick={onDone}>
          Done
        </Button>
        <Button variant="brand" onClick={onInviteAnother}>
          Invite another client <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
